<template>
    <div v-clickaway="close" class="loc-selector">
        <button type="button" :class="triggerClasses" @click="toggle">
            <common-icon name="building" size="semi-small" class="loc-selector__trigger-icon"></common-icon>
            <div class="loc-selector__trigger-text">
                <span class="loc-selector__trigger-name">{{ selected.name }}</span>
                <span class="loc-selector__trigger-address">{{ selected.fullAddress || selected.address || '' }}</span>
            </div>
            <span class="loc-selector__trigger-chevron" :class="{ 'loc-selector__trigger-chevron--open': isOpen }"></span>
        </button>

        <transition name="loc-selector-fade">
            <div v-if="isOpen" class="loc-selector__panel">
                <div class="loc-selector__panel-header">
                    <common-icon name="location" size="semi-small"></common-icon>
                    <span class="loc-selector__panel-title">Select Location</span>
                </div>
                <div class="loc-selector__separator"></div>
                <ul class="loc-selector__list">
                    <li
                        v-for="location in locations"
                        :key="location.id"
                        class="loc-selector__item"
                        :class="{ 'loc-selector__item--active': selected.id === location.id }"
                        @click="selectLocation(location)"
                    >
                        <div class="loc-selector__item-row">
                            <span class="loc-selector__item-name">{{ location.name }}</span>
                            <span v-if="location.isDefault" class="loc-selector__item-badge">Default</span>
                            <span v-if="!location.isAvailabilitySet" class="loc-selector__item-badge--warning">
                                No availability set
                            </span>
                        </div>
                        <span class="loc-selector__item-address">{{ location.fullAddress }}</span>
                        <span v-if="location.phone" class="loc-selector__item-phone">{{ location.phone }}</span>
                    </li>
                </ul>
            </div>
        </transition>
    </div>
</template>

<script>
    export default {
        name: 'location-selector-dropdown',

        data() {
            return {
                isOpen: false,
                selected: { id: 'main', name: 'Main Location', address: '', isAvailabilitySet: true },
                locations: [],
            }
        },

        computed: {
            triggerClasses() {
                return {
                    'loc-selector__trigger': true,
                    'loc-selector__trigger--open': this.isOpen,
                    'loc-selector__trigger--warning': !this.selected.isAvailabilitySet,
                }
            },
        },

        methods: {
            toggle() {
                if (!this.isOpen) {
                    // Re-read the practice from the store so availability flags
                    // are fresh (e.g. right after a schedule was saved).
                    this.loadLocations()
                }
                this.isOpen = !this.isOpen
            },

            close() {
                this.isOpen = false
            },

            selectLocation(location) {
                this.selected = location
                this.isOpen = false
                const query = { ...this.$route.query }
                if (location.id === 'main') {
                    delete query.locationId
                } else {
                    query.locationId = String(location.id)
                }
                this.$router.push({ name: this.$route.name, params: this.$route.params, query }).catch(() => {})
            },

            buildFullAddress(obj) {
                const parts = [obj.address, obj.city, obj.state, obj.zip].filter(Boolean)
                return parts.join(', ')
            },

            loadLocations() {
                const practice = this.$auth.getPractice()
                if (!practice) return

                const mainAddress = this.buildFullAddress(practice)
                const mainLocation = {
                    id: 'main',
                    name: 'Main Location',
                    address: practice.address || '',
                    fullAddress: mainAddress,
                    phone: practice.phone || '',
                    isDefault: true,
                    isAvailabilitySet: practice.isAvailabilitySet !== false,
                }

                const practiceLocations = (practice.locations || []).map((loc) => ({
                    id: loc.id,
                    name: loc.name,
                    address: loc.address || '',
                    fullAddress: this.buildFullAddress(loc),
                    phone: loc.phone || '',
                    isDefault: false,
                    isAvailabilitySet: loc.isAvailabilitySet !== false,
                }))

                this.locations = [mainLocation, ...practiceLocations]
                this.syncSelectedFromRoute()
            },

            syncSelectedFromRoute() {
                const locationId = this.$route.query.locationId
                if (locationId) {
                    const found = this.locations.find((loc) => String(loc.id) === String(locationId))
                    if (found) {
                        this.selected = found
                        return
                    }
                }
                this.selected = this.locations[0] || { id: 'main', name: 'Main Location', address: '' }
            },
        },

        watch: {
            '$route.query.locationId'() {
                this.loadLocations()
            },
        },

        mounted() {
            this.loadLocations()
        },
    }
</script>

<style scoped>
    .loc-selector {
        position: relative;
        flex-shrink: 0;
    }

    .loc-selector__trigger {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 46px;
        min-width: 280px;
        max-width: 380px;
        padding: 0 12px;
        background: #ffffff;
        border: 1.5px solid #d1d5db;
        border-radius: 10px;
        cursor: pointer;
        text-align: left;
        transition: border-color 0.15s;
    }

    .loc-selector__trigger:hover,
    .loc-selector__trigger--open {
        border-color: #6b7280;
    }

    .loc-selector__trigger--warning,
    .loc-selector__trigger--warning:hover,
    .loc-selector__trigger--warning.loc-selector__trigger--open {
        border-color: #ef4444;
    }

    .loc-selector__trigger-icon {
        flex-shrink: 0;
        color: #64748b;
        margin-top: 1px;
    }

    .loc-selector__trigger-text {
        display: flex;
        flex-direction: column;
        gap: 0;
        flex: 1;
        min-width: 0;
        overflow: hidden;
    }

    .loc-selector__trigger-name {
        font-size: 13px;
        font-weight: 400;
        color: #1f2937;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.3;
    }

    .loc-selector__trigger-address {
        font-size: 11px;
        color: #64748b;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.2;
        font-weight: 400;
    }

    .loc-selector__trigger-chevron {
        display: inline-block;
        flex-shrink: 0;
        margin-left: auto;
        width: 7px;
        height: 7px;
        border-right: 1.25px solid #6b7280;
        border-bottom: 1.25px solid #6b7280;
        transform: rotate(45deg) translate(-1px, -1px);
        transition: transform 0.2s;
    }

    .loc-selector__trigger-chevron--open {
        transform: rotate(-135deg) translate(-1px, 2px);
    }

    .loc-selector__panel {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        z-index: 100;
        width: 380px;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
        overflow: hidden;
    }

    .loc-selector__panel-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        color: #374151;
    }

    .loc-selector__panel-title {
        font-size: 14px;
        font-weight: 500;
        color: #111827;
    }

    .loc-selector__separator {
        height: 1px;
        background: #e5e7eb;
    }

    .loc-selector__list {
        list-style: none;
        margin: 0;
        padding: 6px;
        max-height: 340px;
        overflow-y: auto;
    }

    .loc-selector__item {
        display: flex;
        flex-direction: column;
        gap: 3px;
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        transition: background 0.12s;
    }

    .loc-selector__item:hover {
        background: #f9fafb;
    }

    .loc-selector__item--active {
        background: #f3f4f6;
    }

    .loc-selector__item-row {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .loc-selector__item-name {
        font-size: 13px;
        color: #1f2937;
        font-weight: 500;
        line-height: 1.3;
    }

    .loc-selector__item-badge {
        font-size: 11px;
        font-weight: 500;
        color: #ffffff;
        background: #111827;
        padding: 2px 6px;
        border-radius: 4px;
        line-height: 1.4;
        white-space: nowrap;
    }

    .loc-selector__item-badge--warning {
        font-size: 11px;
        font-weight: 500;
        color: #dc2626;
        background: #fee2e2;
        padding: 2px 6px;
        border-radius: 4px;
        line-height: 1.4;
        white-space: nowrap;
    }

    .loc-selector__item-address {
        font-size: 12px;
        color: #6b7280;
        line-height: 1.35;
        font-weight: 400;
    }

    .loc-selector__item-phone {
        font-size: 12px;
        color: #6b7280;
        line-height: 1.35;
        font-weight: 400;
    }

    .loc-selector-fade-enter-active,
    .loc-selector-fade-leave-active {
        transition: opacity 0.15s, transform 0.15s;
    }

    .loc-selector-fade-enter,
    .loc-selector-fade-leave-to {
        opacity: 0;
        transform: translateY(-4px);
    }

    @media (max-width: 1024px) {
        .loc-selector {
            width: 100%;
        }
        .loc-selector__trigger {
            width: 100%;
            max-width: unset;
            min-width: unset;
        }
        .loc-selector__panel {
            width: 100%;
        }
    }

    @media (max-width: 425px) {
        .loc-selector__trigger {
            height: 42px;
            gap: 8px;
            padding: 0 10px;
        }
        .loc-selector__trigger-name {
            font-size: 13px;
        }
        .loc-selector__trigger-address {
            font-size: 10px;
        }
        .loc-selector__panel {
            width: calc(100vw - 24px);
            left: auto;
            right: 0;
        }
    }
</style>
